'use client';

import { useAuth, useOrganizationList } from '@clerk/clerk-react';
import { useEffect, useSyncExternalStore } from 'react';

import {
  getActiveOrganizationAttempt,
  subscribeToActiveOrganizationAttempt,
} from '../organization/activeOrganizationAttempt';
import {
  clearClerkLoadDeadline,
  getClerkLoadExpired,
  startClerkLoadDeadline,
  subscribeToClerkLoadDeadline,
} from '../organization/clerkLoadDeadline';
import type { OrganizationMembership } from '../organization/dashboardState';

/**
 * useActiveOrganizationStatus reports where the Clerk session stands on organization membership,
 * with no side effects. `useEnsureActiveOrganization` (the root) reads it to decide whether to
 * auto-select an organization; the dashboard reads it to decide what to render.
 *
 * Split out from the root's effect hook deliberately: calling that hook a second time would run
 * its select-and-`setActive` effect twice and race two `setActive` calls.
 *
 * Every state it returns must be leaveable — that is the defect this surface exists to remove.
 * `pending` is therefore only reported while something is actually still running: once the
 * auto-select reports `failed`, a user with memberships but no active organization resolves to
 * `noActive`, which the dashboard renders as a terminal card with a working control.
 */
export function useActiveOrganizationStatus(): OrganizationMembership {
  const { isLoaded: authLoaded, isSignedIn, orgId } = useAuth();
  const { isLoaded: listLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: false },
  });
  const attempt = useSyncExternalStore(
    subscribeToActiveOrganizationAttempt,
    getActiveOrganizationAttempt,
    getActiveOrganizationAttempt
  );
  const clerkLoadExpired = useSyncExternalStore(
    subscribeToClerkLoadDeadline,
    getClerkLoadExpired,
    getClerkLoadExpired
  );

  // Arm the bound while the client is unloaded and release it once it arrives, so a later render
  // cannot read an expiry left over from a load that has since completed.
  useEffect(() => {
    if (!authLoaded || !listLoaded) {
      startClerkLoadDeadline();
      return;
    }
    clearClerkLoadDeadline();
  }, [authLoaded, listLoaded]);

  if (!authLoaded || !listLoaded) {
    // The vendor reports an error status for a client that fails to load, but nothing for one that
    // hangs, so this term has no terminal transition of its own. The deadline supplies it: past it,
    // the caller renders a control instead of continuing to wait on a client that is not coming.
    return clerkLoadExpired ? 'unavailable' : 'pending';
  }
  // Signed out is not this hook's concern — the caller routes to sign-in. Reporting `pending`
  // would strand it, so report the honest "no organization" instead.
  if (!isSignedIn) {
    return 'none';
  }
  if (orgId) {
    return 'active';
  }

  const memberships = userMemberships?.data;
  if (!memberships || memberships.length === 0) {
    return 'none';
  }
  // Memberships exist but none is active. Only an auto-select actually in flight justifies
  // waiting; every other case is terminal and gets a card with a working control.
  //
  // Treating anything other than 'inFlight' as waiting would be wrong, because 'idle' covers
  // two situations that are not waiting: no attempt was ever started, and an attempt resolved
  // without producing an organization id. The second is reachable — Clerk can resolve setActive
  // while the session token's organization claim stays null, which is the condition the root's
  // auto-select exists to repair. That guard keys on session.lastActiveOrganizationId, so it
  // then sees no work to do and never runs again, leaving no writer to move the state on.
  return attempt === 'inFlight' ? 'pending' : 'noActive';
}
